import { useCallback, useEffect, useRef, useState } from 'react';
import type { Industry, Sector, SubIndustry } from '@biasafe.models';

interface GICSData {
  sectors: ReadonlyArray<Sector>;
  industries: ReadonlyArray<Industry>;
  subIndustries: ReadonlyArray<SubIndustry>;
  initialExcludedCodes?: {
    sectors: Array<string>;
    industries: Array<string>;
    subIndustries: Array<string>;
  };
  onUpdate?: (excluded: {
    sectors: Array<string>;
    industries: Array<string>;
    subIndustries: Array<string>;
  }) => void;
}

export function useGICSExclusions({
  sectors,
  industries,
  subIndustries,
  initialExcludedCodes,
  onUpdate,
}: GICSData) {
  // Local `excluded` mirrors the store's GICS codes. Re-sync whenever the store
  // content differs from what we last mirrored — including an EXTERNAL reset:
  // switching the universe clears the store's GICS exclusions, and a one-shot
  // init guard used to leave the PREVIOUS universe's exclusions stranded here
  // (and the next toggle re-pushed them onto the new universe's backtest).
  // Keyed on content, not identity, so echoes of our own toggles re-sync to
  // identical codes — no thrash, no reverting an in-progress toggle.
  //
  // THE SET AND THE KEY IT CAME FROM ARE ONE VALUE, and that is load-bearing.
  // They were a state and a ref, and the mirror effect wrote the ref BEFORE the
  // push effect ran in the same commit — so on mount the push effect read
  // "already synced" while `excluded` was still its empty initial value, wrote
  // that emptiness over the saved codes, and the two then ping-ponged: mirror
  // restores from the store, push clobbers it with the previous render's set,
  // forever. Opening a strategy that excluded any sector took the whole
  // configuration tier down with "Maximum update depth exceeded" (thrown from
  // the setState below, which is what named this hook); a strategy that
  // excluded nothing was fine, which is what made it look intermittent.
  //
  // Held together, the push effect can only ever run against a set a completed
  // sync produced.
  const [mirror, setMirror] = useState<{
    key: string | null;
    codes: Set<string>;
  }>({ key: null, codes: new Set() });
  const excluded = mirror.codes;

  const initialCodesKey = initialExcludedCodes
    ? [
        ...initialExcludedCodes.sectors,
        ...initialExcludedCodes.industries,
        ...initialExcludedCodes.subIndustries,
      ]
        .sort()
        .join('|')
    : '';

  useEffect(() => {
    if (sectors.length === 0) return; // wait for reference data
    setMirror((prev) =>
      prev.key === initialCodesKey
        ? prev
        : {
            key: initialCodesKey,
            codes: new Set(initialCodesKey ? initialCodesKey.split('|') : []),
          },
    );
  }, [initialCodesKey, sectors.length]);

  // What the hook last rendered, for the edit path to read without becoming a
  // new function on every change.
  const mirrorRef = useRef(mirror);
  mirrorRef.current = mirror;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const listsRef = useRef({ sectors, industries, subIndustries });
  listsRef.current = { sectors, industries, subIndustries };

  // A LOCAL EDIT WRITES TO THE STORE. NOTHING ELSE DOES.
  //
  // The push used to be an effect on the mirrored set, which made this a
  // two-way sync: store to local, local to store. Both effects run in the same
  // commit, so each acted on a value the other had already invalidated — the
  // push wrote the mirror from the render before, the mirror re-read the store
  // the push had just overwritten, and the two ran 180 degrees out of phase
  // forever. Opening a strategy that excluded a sector alternated the store
  // between ["311"] and [] until React gave up with "Maximum update depth
  // exceeded", taking the whole configuration tier down with it.
  //
  // One direction is automatic (the mirror below, store to local) and one is
  // explicit (here, and only from a user's toggle or reset). There is no cycle
  // left to fall out of step.
  const applyEdit = useCallback((compute: (prev: Set<string>) => Set<string>) => {
    const prev = mirrorRef.current.codes;
    const codes = compute(prev);
    if (codes === prev) return;
    setMirror((m) => ({ key: m.key, codes }));

    const { sectors: sx, industries: ix, subIndustries: gx } = listsRef.current;
    onUpdateRef.current?.({
      sectors: sx.filter((s) => codes.has(s.code)).map((s) => s.code),
      industries: ix.filter((i) => codes.has(i.code)).map((i) => i.code),
      subIndustries: gx.filter((si) => codes.has(si.code)).map((si) => si.code),
    });
  }, []);

  const findItemByCode = (code: string) => {
    const sector = sectors.find((s) => s.code === code);
    if (sector) return { level: 'sector' as const, item: sector };
    const industry = industries.find((i) => i.code === code);
    if (industry) return { level: 'industry' as const, item: industry };
    const subIndustry = subIndustries.find((si) => si.code === code);
    if (subIndustry)
      return { level: 'sub-industry' as const, item: subIndustry };
    return null;
  };

  const toggle = useCallback(
    (codes: Array<string>) => {
      applyEdit((prev) => {
        const next = new Set(prev);
        // One intent for the whole call: remove only when EVERY code is already
        // excluded, otherwise exclude. For a single code this is identical to the
        // old per-code toggle; for a collapsed group (several renumbered codes
        // standing for one classification) it flips them all together instead of
        // leaving the group half-excluded.
        const isExcluding = !codes.every((c) => prev.has(c));
        for (const code of codes) {
          const found = findItemByCode(code);
          if (!found) return prev;

          if (found.level === 'sector') {
            const children = industries.filter(
              (i) => i.sectorCode === found.item.code,
            );
            const grandchildren = subIndustries.filter(
              (si) => si.sectorCode === found.item.code,
            );
            if (isExcluding) {
              next.add(code);
              for (const c of children) next.add(c.code);
              for (const gc of grandchildren) next.add(gc.code);
            } else {
              next.delete(code);
              for (const c of children) next.delete(c.code);
              for (const gc of grandchildren) next.delete(gc.code);
            }
          } else if (found.level === 'industry') {
            const children = subIndustries.filter(
              (si) => si.industryCode === found.item.code,
            );
            if (isExcluding) {
              next.add(code);
              for (const c of children) next.add(c.code);
            } else {
              next.delete(code);
              for (const c of children) next.delete(c.code);
              const sector = sectors.find(
                (s) => s.code === found.item.sectorCode,
              );
              if (sector) next.delete(sector.code);
            }
          } else {
            if (isExcluding) {
              next.add(code);
            } else {
              next.delete(code);
              const si = found.item;
              const industry = industries.find(
                (i) => i.code === si.industryCode,
              );
              if (industry) next.delete(industry.code);
              const sector = sectors.find((s) => s.code === si.sectorCode);
              if (sector) next.delete(sector.code);
            }
          }
        }

        return next;
      });
    },
    [sectors, industries, subIndustries, applyEdit],
  );

  // Clear all GICS exclusions (the section "Reset") — an edit like any other,
  // so it writes straight through.
  const clear = useCallback(
    () => applyEdit((prev) => (prev.size === 0 ? prev : new Set<string>())),
    [applyEdit],
  );

  return { excluded, toggle, clear };
}
